import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  setDoc,
  type DocumentData,
} from 'firebase/firestore'
import { getFirebaseServices } from '../lib/firebase'
import type { Invoice, WorkProject } from '../types'

function isText(value: unknown, max = 500): value is string {
  return typeof value === 'string' && value.length <= max
}

function isProject(value: DocumentData): value is WorkProject {
  return (
    isText(value.id, 128) &&
    isText(value.number, 100) &&
    isText(value.name, 1000) &&
    isText(value.clientId, 128) &&
    isText(value.clientNameSnapshot, 300) &&
    (value.status === 'active' || value.status === 'archived') &&
    isText(value.createdAt, 64) &&
    isText(value.updatedAt, 64)
  )
}

function isInvoice(value: DocumentData): value is Invoice {
  return (
    isText(value.id, 128) &&
    isText(value.number, 100) &&
    isText(value.clientId, 128) &&
    isText(value.clientNameSnapshot, 300) &&
    (value.projectId === null || isText(value.projectId, 128)) &&
    isText(value.projectNameSnapshot, 1000) &&
    Number.isSafeInteger(value.amountKopecks) &&
    value.amountKopecks >= 0 &&
    value.currency === 'RUB' &&
    isText(value.issuedOn, 10) &&
    ['issued', 'shipped', 'paid', 'cancelled'].includes(value.status) &&
    (value.shippedOn === null || isText(value.shippedOn, 10)) &&
    Number.isInteger(value.paymentTermsDays) &&
    value.paymentTermsDays >= 0 &&
    value.paymentTermsDays <= 365 &&
    (value.paidOn === null || isText(value.paidOn, 10)) &&
    isText(value.createdAt, 64) &&
    isText(value.updatedAt, 64)
  )
}

function userCollection(userId: string, name: 'projects' | 'invoices') {
  return collection(getFirebaseServices().firestore, 'users', userId, name)
}

export function watchProjects(
  userId: string,
  onItems: (items: WorkProject[]) => void,
  onError: () => void,
): () => void {
  return onSnapshot(
    userCollection(userId, 'projects'),
    (snapshot) => {
      const items = snapshot.docs
        .map((item) => item.data())
        .filter(isProject)
        .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt))
      onItems(items)
    },
    onError,
  )
}

export function watchInvoices(
  userId: string,
  onItems: (items: Invoice[]) => void,
  onError: () => void,
): () => void {
  return onSnapshot(
    userCollection(userId, 'invoices'),
    (snapshot) => {
      const items = snapshot.docs
        .map((item) => item.data())
        .filter(isInvoice)
        .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt))
      onItems(items)
    },
    onError,
  )
}

export async function saveProject(userId: string, project: WorkProject): Promise<void> {
  await setDoc(doc(getFirebaseServices().firestore, 'users', userId, 'projects', project.id), project)
}

export async function deleteProject(userId: string, projectId: string): Promise<void> {
  await deleteDoc(doc(getFirebaseServices().firestore, 'users', userId, 'projects', projectId))
}

export async function saveInvoice(userId: string, invoice: Invoice): Promise<void> {
  await setDoc(doc(getFirebaseServices().firestore, 'users', userId, 'invoices', invoice.id), invoice)
}

export async function deleteInvoice(userId: string, invoiceId: string): Promise<void> {
  await deleteDoc(doc(getFirebaseServices().firestore, 'users', userId, 'invoices', invoiceId))
}
